import type {
  CatalogAdapter,
  CatalogStore,
  NormalizedProduct,
  Result,
} from './types';

const PAGE_SIZE = 250;
const MAX_PAGES = 50;

interface ShopifyImage {
  src?: string | null;
}

interface ShopifyVariant {
  price?: string | number | null;
}

interface ShopifyProduct {
  id: number | string;
  handle: string;
  title: string;
  body_html?: string | null;
  variants?: ShopifyVariant[] | null;
  images?: ShopifyImage[] | null;
  image?: ShopifyImage | null;
}

interface ShopifyProductsPage {
  products?: ShopifyProduct[] | null;
}

/**
 * Reads a Shopify storefront's public `/products.json` feed (BAL-449).
 *
 * Every Shopify store exposes `<store>/products.json?limit=&page=` unauthenticated
 * — up to 250 products/page, paginated by `page` until a short/empty page.
 * Prices are strings ("39.00"); the feed carries no currency, so
 * `NormalizedProduct.currency` is left null and the sync service defaults it.
 */
export class ShopifyAdapter implements CatalogAdapter {
  async fetchProducts(
    store: CatalogStore,
  ): Promise<Result<NormalizedProduct[]>> {
    const base = store.url.replace(/\/+$/, '');
    const products: NormalizedProduct[] = [];

    try {
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const res = await fetch(
          `${base}/products.json?limit=${PAGE_SIZE}&page=${page}`,
          { headers: { 'User-Agent': 'BalleeCatalogSync/1.0' } },
        );

        if (!res.ok) {
          return {
            success: false,
            error: new Error(
              `Shopify products.json fetch failed (${res.status}) for ${base} page ${page}`,
            ),
          };
        }

        const json = (await res.json()) as ShopifyProductsPage;
        const batch = json.products ?? [];
        if (batch.length === 0) break;

        for (const product of batch) {
          products.push(this.normalize(product, base));
        }

        if (batch.length < PAGE_SIZE) break;
      }

      return { success: true, data: products };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err : new Error('Shopify fetch failed'),
      };
    }
  }

  private normalize(product: ShopifyProduct, base: string): NormalizedProduct {
    const rawPrice = product.variants?.[0]?.price;
    const priceAmount =
      rawPrice === null || rawPrice === undefined
        ? null
        : (() => {
            const parsed = Number.parseFloat(String(rawPrice));
            return Number.isFinite(parsed) ? parsed : null;
          })();

    const imageUrl = product.images?.[0]?.src ?? product.image?.src ?? null;

    return {
      externalId: String(product.id),
      handle: product.handle,
      title: product.title,
      description: product.body_html ?? null,
      priceAmount,
      currency: null,
      imageUrl,
      productUrl: `${base}/products/${product.handle}`,
    };
  }
}
